import {FieldPath, useController} from "react-hook-form";
import React, {DetailedHTMLProps, InputHTMLAttributes} from "react";
import {FieldValues} from "react-hook-form/dist/types/fields";
import {maskFormatter, OnInsertFn, useFormattedInput} from "react-formatted-input-hook";
import {Control} from "react-hook-form/dist/types";
import moment from "moment/moment";
import InputEx, {InputExProps} from "./InputEx";

interface Props<T extends FieldValues> extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    control: Control<T>,
    name: FieldPath<T>,
    required?: boolean,
    min?: string,
    max?: string,
    validate?: (val: string) => boolean | string, // TO DO implementare nello stesso codice della validate interna
    deps?: string | string[]
    label?: string,
    addOnBefore?: JSX.Element;
    addOnAfter?: JSX.Element;
    addOnStyle?: 'inline';
}

export function InputDate<T extends FieldValues>(props: Props<T>) {
    const optimize = true;
    const {
        control,
        name,
        type = 'text',
        required,
        min,
        max,
        validate,
        deps,
        label,
        addOnBefore,
        addOnAfter,
        addOnStyle,
        ...rest
    } = props;
    const localRequired = required || false;
    let rules = {
        deps: props.deps,
        validate: {
            validate: (v: string) => {
                if (v && v.length > 0) {
                    return moment(v, 'YYYY-MM-DD').isValid();
                } else
                    return true;
            },
            required: (v: string) => {
                if (localRequired) {
                    return v && v.length > 0 ? true : false;
                } else
                    return true;
            },
            min: (v: string) => {
                if (props.min) {
                    const value = moment(v, 'YYYY-MM-DD');
                    const min = moment(props.min, 'YYYY-MM-DD');
                    if (value.isValid() && min.isValid())
                        return value.isSameOrAfter(min) ? true : 'La data deve essere maggiore o uguale a ' + min.format('DD/MM/YYYY');
                } else
                    return true;
            },
            max: (v: string) => {
                if (props.max) {
                    const value = moment(v, 'YYYY-MM-DD');
                    const max = moment(props.max, 'YYYY-MM-DD');
                    if (value.isValid() && max.isValid())
                        return value.isSameOrBefore(max) ? true : 'La data deve essere minore o uguale a ' + max.format('DD/MM/YYYY');
                } else
                    return true;
            },
        }
    }

    const {
        field,
        fieldState: {error, isTouched, isDirty},
        formState: {touchedFields, dirtyFields, errors}
    } = useController({
        name,
        control,
        rules,
    });
    let message: string;
    if (error) {
        message = error.message && error.message.length > 0 ? error.message : (error.type === 'required' ? 'Campo obbligatorio.' : 'Valore non corretto.');
    } else
        message = errors ? (errors[name!] ? errors[name!]?.message : '???') as string : '';

    const from = (value: string) => {
        if (value && value.length > 0)
            return moment(value, 'YYYY-MM-DD').format('DDMMYYYY')
        return '';
    }

    const to = (value: string) => {
        if (value && value.length > 0) {
            const d = moment(value, 'DD/MM/YYYY');
            return d.format('YYYY-MM-DD');
        }
        return '';
    }

    const opts = maskFormatter({
        mask: "##/##/####",
        allowedChars: /[0-9]/,
        liveUpdate: true,
        value: from(field.value),
        onChange: (value, formattedValue) => field.onChange(to(formattedValue)),
    });

    const formattedInput = useFormattedInput(
        {
            ...opts,
            onInsert: (p) => {
                const onInsert = opts.onInsert as OnInsertFn;
                if (optimize) {
                    const {insert, caret, value} = p;
                    const zero = {char: '0', insert, caret, value};
                    console.log(caret.left)
                    if (caret.left === 0) {
                        if (parseInt(p.char) > 3) {
                            onInsert(zero as typeof p);
                        }
                    }
                    if (caret.left === 2) {
                        if (parseInt(p.char) > 1) {
                            onInsert(zero as typeof p);
                        }
                    }
                }
                onInsert(p);
            }
        }
    )

    const className = InputExProps.getClass({...props, error: error});

    const input = (<>
        <input id={name}
               type={type}
               {...formattedInput.props}
               onChange={(e) => { // utile per la valorizzazione da autocomplete...
                   formattedInput.setValue(from(to(e.target.value)), 0);
               }}
               {...rest}
               className={className}
        />
        {errors && errors[name!] && <p className="text-rose-700">{message}</p>}
    </>);
    return <InputEx {...{...props, input: input}}/>
}

export default InputDate;